import React from 'react'
import {
  Joyride,
  EventData,
  STATUS,
  EVENTS,
  ACTIONS,
  Step,
} from 'react-joyride'
import { useNavigate } from 'react-router-dom'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  selectRoles,
  selectBoundTreeStatus,
} from 'src/v3/settings/settings.slice'
import { paletteColorsDark } from 'src/theme'
import {
  selectActiveTourID,
  activeTourCleared,
  saveTourCompletion,
} from '../tours.slice'
import { getTourByID } from '../index'
import { TourStep } from '../types'
import { TourTooltip } from './tour-tooltip'

export let TourProvider = ({ children }: { children: React.ReactNode }) => {
  let dispatch = useAppDispatch()
  let navigate = useNavigate()
  let activeTourID = useAppSelector(selectActiveTourID)
  let roles = useAppSelector(selectRoles)
  let boundTreeStatus = useAppSelector(selectBoundTreeStatus)

  let activeTour = activeTourID ? getTourByID(activeTourID) : null

  let passesFilter = (step: TourStep) =>
    (boundTreeStatus !== 'limited' ||
      (step.allowInLimited ?? activeTour?.allowInLimited)) &&
    (!step.requiredRoles || roles.some((r) => step.requiredRoles!.includes(r)))

  // filteredDefs keeps our TourStep definitions (with navigateTo etc.) in the same
  // index order as filteredSteps, so handleEvent can look up navigateTo by Joyride's index.
  let filteredDefs = activeTour ? activeTour.steps.filter(passesFilter) : []

  let filteredSteps: Step[] = filteredDefs.map((step) => ({
    target: step.target,
    content: step.content,
    title: step.title,
    placement: step.placement,
    skipBeacon: step.disableBeacon ?? true,
    blockTargetInteraction: !(step.spotlightClicks ?? false),
  }))

  let handleEvent = (data: EventData) => {
    let { type, action, index, status } = data

    // Navigate to the next route when the user clicks Next on a step with navigateTo
    if (
      type === EVENTS.STEP_AFTER &&
      (action === ACTIONS.NEXT || action === ACTIONS.COMPLETE)
    ) {
      let stepDef = filteredDefs[index]
      if (stepDef?.navigateTo) {
        let route =
          typeof stepDef.navigateTo === 'function'
            ? stepDef.navigateTo()
            : stepDef.navigateTo
        navigate(route)
      }
    }

    let finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED]
    if (finishedStatuses.includes(status)) {
      if (status === STATUS.FINISHED && activeTour?.completionRoute) {
        navigate(activeTour.completionRoute)
      }
      dispatch(
        saveTourCompletion({
          tourID: activeTour!.id,
          completedVersion: activeTour!.version,
        }),
      ).then(() => dispatch(activeTourCleared()))
    }
  }

  return (
    <>
      {activeTour && filteredSteps.length > 0 && (
        <Joyride
          steps={filteredSteps}
          run
          continuous
          tooltipComponent={TourTooltip}
          onEvent={handleEvent}
          options={{
            arrowColor: paletteColorsDark.background.paper,
            overlayColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 10000,
            scrollOffset: 100,
          }}
        />
      )}
      {children}
    </>
  )
}
